/**
 * Phase J.4a (2026-07-29) — SendAchActivationButton
 *
 * Sits on AdminCustomerDetailPage header. Renders one of:
 *   • Nothing (customer already has a verified bank OR no signed ACH form)
 *   • "Send ACH activation link" (default state)
 *   • "Activation link sent" + inline "Resend" (state after send, before verify)
 *   • "ACH verified" badge (once verified)
 *
 * We compute the state from the customer doc alone (fields set by the
 * J.4a endpoint) + a lazy check for a signed ACH form via the customer's
 * contracts. Simpler: rely on the endpoint's own 400 to reject invalid
 * clicks — button always shows when NOT verified.
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Send, RotateCcw, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";


export default function SendAchActivationButton({ customer, onSent }) {
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [isResend, setIsResend] = useState(false);

    if (!customer) return null;

    const verified = Boolean(customer.default_payment_method_id);
    const sentAt = customer.stripe_ach_activation_sent_at;
    const usedAt = customer.stripe_ach_activation_link_used_at;

    if (verified) {
        return (
            <span
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 text-xs font-semibold px-2.5 py-1"
                data-testid="customer-ach-verified-chip"
            >
                <ShieldCheck size={12} /> ACH verified
            </span>
        );
    }

    const email = customer.billing_contact_email || customer.primary_contact_email;
    const disabled = !email;

    const doSend = async (force) => {
        setBusy(true);
        try {
            const { data } = await api.post(
                `/admin/customers/${customer.id}/send-ach-activation${force ? "?force=true" : ""}`,
            );
            toast.success(`Activation link sent (expires ${data.expires_at?.slice(0, 10)}).`);
            setConfirmOpen(false);
            onSent && onSent();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const label = sentAt && !usedAt
        ? `Activation link sent ${sentAt.slice(0, 10)}`
        : "Send ACH activation link";
    const Icon = sentAt && !usedAt ? RotateCcw : Send;

    return (
        <>
            <Button
                variant="outline"
                onClick={() => { setIsResend(Boolean(sentAt) && !usedAt); setConfirmOpen(true); }}
                disabled={disabled}
                title={disabled ? "Customer has no billing email on file" : ""}
                className="gap-1.5"
                data-testid="customer-send-ach-activation-btn"
            >
                <Icon size={14} /> {sentAt && !usedAt ? "Resend activation link" : "Send ACH activation link"}
            </Button>
            {sentAt && !usedAt && (
                <span className="text-[11px] text-slate-500 self-center" data-testid="customer-ach-activation-note">
                    {label}
                </span>
            )}
            <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                <DialogContent data-testid="customer-send-ach-modal">
                    <DialogHeader>
                        <DialogTitle className="font-display text-xl">
                            Send ACH activation link?
                        </DialogTitle>
                        <DialogDescription>
                            {isResend
                                ? `A new activation link will be emailed to ${email}, invalidating any prior link.`
                                : `An activation link will be emailed to ${email}. They'll verify their bank via Stripe Financial Connections; auto-pay unlocks once verified.`}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setConfirmOpen(false)} disabled={busy} data-testid="customer-send-ach-cancel">
                            Cancel
                        </Button>
                        <Button
                            onClick={() => doSend(isResend)}
                            disabled={busy}
                            className="bg-blue-900 hover:bg-blue-800 gap-2"
                            data-testid="customer-send-ach-confirm"
                        >
                            {busy ? (<><Loader2 size={14} className="animate-spin" /> Sending…</>) : (isResend ? "Resend link" : "Send link")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
